import React, { useState, useEffect, useRef, useCallback } from 'react'; // Added useCallback
import axios from 'axios';
import ConversationsList from './ConversationsList';
import MessageList from './MessageList';
import { useSocket } from '../../contexts/SocketContext'; // Import useSocket

const ChatRoom = () => {
  const { socket, isConnected } = useSocket(); // Get socket instance and connection status
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true); // Start loading initially
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [chatUsers, setChatUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isTeamChat, setIsTeamChat] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({
    teamChat: 0,
    dm: 0,
    total: 0
  });

  const messagesEndRef = useRef(null);
  const userListRef = useRef(null);
  const messagesContainerRef = useRef(null); // Ref for scroll container
  const [isAtBottom, setIsAtBottom] = useState(true); // State for scroll tracking

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const userJson = localStorage.getItem('user');

        if (userJson) {
          setUser(JSON.parse(userJson));
        } else {
          const config = { headers: { 'x-auth-token': token } };
          const res = await axios.get('/api/auth/me', config);
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Error loading user profile. Please try refreshing the page.');
        setLoading(false); // Stop loading if user fetch fails
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch conversations (wrapped in useCallback)
  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.get('/api/chat/conversations', config);
      const unreadRes = await axios.get('/api/chat/unread', config);
      const unreadByUserId = {};

      if (unreadRes.data.dmConversations) {
        unreadRes.data.dmConversations.forEach(convo => {
          unreadByUserId[convo.user_id] = convo.unread_count;
        });
      }

      const updatedConversations = res.data.conversations.map(convo => ({
        ...convo,
        unread_count: unreadByUserId[convo.user_id] || 0
      }));

      setConversations(updatedConversations);

      // Update global unread counts, respecting current view
      setUnreadCounts({
        teamChat: isTeamChat ? 0 : unreadRes.data.teamChatUnreadCount,
        dm: unreadRes.data.dmUnreadCount,
        total: isTeamChat
          ? unreadRes.data.dmUnreadCount
          : unreadRes.data.teamChatUnreadCount + unreadRes.data.dmUnreadCount
      });
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  }, [isTeamChat]); // Dependency on isTeamChat

  // Mark messages as read when viewing a conversation (wrapped in useCallback)
  const markMessagesAsRead = useCallback(async () => {
    // Use a static property on the function itself to track running state
    if (markMessagesAsRead.isRunning) return;
    markMessagesAsRead.isRunning = true;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } };
      const payload = isTeamChat ? {} : { conversation_id: selectedConversation?.user_id };

      if (!isTeamChat && !selectedConversation?.user_id) {
         console.log("Skipping mark as read: No DM conversation selected");
         markMessagesAsRead.isRunning = false;
         return;
      }

      await axios.post('/api/chat/mark-read', payload, config);

      // Update counts after marking read
      await fetchConversations();
    } catch (err) {
      console.error('Error marking messages as read:', err);
    } finally {
      markMessagesAsRead.isRunning = false;
    }
  }, [isTeamChat, selectedConversation, fetchConversations]); // Add dependencies

  // Fetch messages for current conversation (team chat or DM)
  const fetchMessages = useCallback(async () => {
    // This function now assumes 'user' is available because it's called by an effect that depends on 'user'
    if (!user) {
        console.warn("fetchMessages called before user was loaded.");
        return; // Should not happen if called from the correct effect
    }
    try {
      setLoading(true); // Set loading true when starting fetch
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const endpoint = isTeamChat
        ? '/api/chat'
        : `/api/chat?recipient_id=${selectedConversation?.user_id}`;

      // Avoid fetching if DM selected but no user_id yet
      if (!isTeamChat && !selectedConversation?.user_id) {
          setMessages([]); // Clear messages if no valid DM selected
          setLoading(false); // Stop loading
          return;
      }

      const res = await axios.get(endpoint, config);
      setMessages(res.data.messages.reverse()); // Reverse to display oldest first
      setError('');
      markMessagesAsRead(); // Mark as read after fetching

    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
        setLoading(false); // Always stop loading (success or error)
    }
  }, [isTeamChat, selectedConversation, user, markMessagesAsRead]); // Keep user dependency

  // Fetch chat users (for user list)
  const fetchChatUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.get('/api/chat/users', config);
      setChatUsers(res.data);
    } catch (err) {
      console.error('Error fetching chat users:', err);
    }
  };

  // Load initial static data (users, conversations) on mount
  useEffect(() => {
    const initialFetch = async () => {
      await fetchChatUsers();
      await fetchConversations();
    };
    initialFetch();

    // Poll for users every 60 seconds
    const userInterval = setInterval(fetchChatUsers, 60000);
    return () => clearInterval(userInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Fetch messages when user is loaded or conversation changes
  useEffect(() => {
    if (user) { // Only fetch messages if user is loaded
      fetchMessages();
    }
    // This effect depends on user, isTeamChat, and selectedConversation
    // It will run when the user loads initially, and when the chat view changes
  }, [user, isTeamChat, selectedConversation, fetchMessages]);


  // Check if user is at bottom of chat (used for scroll handling)
  const checkIfAtBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const atBottom = scrollHeight - scrollTop - clientHeight < 100;
      setIsAtBottom(atBottom);
    }
  }, []); // No dependencies needed here

  // WebSocket listener for new messages and reactions
  useEffect(() => {
    if (socket && isConnected && user) {
      console.log('ChatRoom: Socket connected, setting up listeners...');

      const handleNewMessage = (newMessageData) => {
        console.log('ChatRoom: Received new_message event:', newMessageData);

        // Check scroll position *before* potentially adding the message and scrolling
        let shouldScroll = false;
        if (messagesContainerRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
          shouldScroll = scrollHeight - scrollTop - clientHeight < 100; // Check if near bottom
        }

        // Check if the message belongs to the current view
        const isCurrentTeamChatMessage = isTeamChat && !newMessageData.recipient_id;
        const isCurrentDmMessage = !isTeamChat && selectedConversation &&
          ((newMessageData.user_id === user.id && newMessageData.recipient_id === selectedConversation.user_id) ||
           (newMessageData.user_id === selectedConversation.user_id && newMessageData.recipient_id === user.id));

        // Update state based on whether the message belongs to the current view
        if (isCurrentTeamChatMessage || isCurrentDmMessage) {
          setMessages(prevMessages => [...prevMessages, newMessageData]);
          markMessagesAsRead();
        } else {
          fetchConversations(); // Update conversation list/unread counts if not viewing
        }

        // Scroll only if we determined the user was at the bottom *before* the message arrived
        if (shouldScroll) {
            // Use timeout to allow state update and render before scrolling
            setTimeout(() => {
                if (messagesEndRef.current) {
                    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 0);
        }
      };
      
      // Handle reaction updates
      const handleReactionUpdate = (data) => {
        console.log('ChatRoom: Received message_reaction_updated event:', data);
        
        // Update the message with new reactions
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.id === data.message_id 
              ? { ...msg, reactions: data.reactions }
              : msg
          )
        );
      };

      socket.on('new_message', handleNewMessage);
      socket.on('message_reaction_updated', handleReactionUpdate);
      console.log('ChatRoom: message listeners attached.');

      // Cleanup listeners on component unmount or socket change
      return () => {
        if (socket) {
          socket.off('new_message', handleNewMessage);
          socket.off('message_reaction_updated', handleReactionUpdate);
          console.log('ChatRoom: message listeners removed.');
        }
      };
    } else {
      console.log('ChatRoom: Socket not connected or user not loaded, listeners not attached.');
    }
    // REMOVED isAtBottom from dependency array
  }, [socket, isConnected, user, isTeamChat, selectedConversation, markMessagesAsRead, fetchConversations]);


  // Handle scroll events
  const handleScroll = useCallback(() => {
    checkIfAtBottom();
  }, [checkIfAtBottom]);

  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]); // Dependency on the callback

  // Handle click outside to close user list
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userListRef.current && !userListRef.current.contains(event.target) &&
          !event.target.closest('button[data-userlist-toggle]')) {
        setShowUserList(false);
      }
    };
    const handleEscKey = (event) => {
      if (event.key === 'Escape') { setShowUserList(false); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  // Scroll to bottom only if user was already at bottom or when component first loads/message added
  useEffect(() => {
    // This effect now only depends on `messages` and `isAtBottom` state
    // It will trigger scrolling if `isAtBottom` is true when `messages` changes
    if (isAtBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAtBottom]); // Dependency on messages ensures scroll after new message added

  // Handle sending a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !isConnected) return; // Also check isConnected

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Content-Type': 'application/json', 'x-auth-token': token } };
      const messageData = {
        content: newMessage,
        ...(isTeamChat ? {} : { recipient_id: selectedConversation.user_id })
      };

      // Check scroll position *before* sending
      checkIfAtBottom();

      await axios.post('/api/chat', messageData, config); // Send message via API

      setNewMessage(''); // Clear input immediately

      // No need to call fetchMessages() here, WebSocket 'new_message' event will handle UI update.
      // Refresh conversations list if it was a DM to update last message preview
      if (!isTeamChat) {
        fetchConversations();
      }

    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  // Handle deleting a message
  const handleDeleteMessage = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      await axios.delete(`/api/chat/${id}`, config);
      // Remove the message from the state optimistically
      setMessages(prevMessages => prevMessages.filter(message => message.id !== id));
      // TODO: Optionally emit a 'message_deleted' event via WebSocket if others need to know
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message. Please try again.');
    }
  };
  
  // Handle adding emoji reaction
  const handleAddReaction = async (messageId, emoji) => {
    try {
      // Optimistic update
      setMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.id === messageId) {
            const reactions = [...(msg.reactions || [])];
            // Check if reaction already exists
            const existingReactionIndex = reactions.findIndex(r => r.emoji === emoji);
            
            if (existingReactionIndex >= 0) {
              // Update existing reaction if user hasn't already reacted
              const reaction = reactions[existingReactionIndex];
              if (!reaction.users.some(u => u.id === user?.id)) {
                reactions[existingReactionIndex] = {
                  ...reaction,
                  count: reaction.count + 1,
                  users: [...reaction.users, { id: user?.id, name: user?.name }]
                };
              }
            } else {
              // Add new reaction
              reactions.push({
                emoji,
                count: 1,
                users: [{ id: user?.id, name: user?.name }]
              });
            }
            
            return { ...msg, reactions };
          }
          return msg;
        });
      });
      
      // Make API call
      const token = localStorage.getItem('token');
      const config = { 
        headers: { 
          'x-auth-token': token,
          'Content-Type': 'application/json'
        } 
      };
      await axios.post(`/api/chat/${messageId}/reactions`, { emoji }, config);
      // The reaction update will also be handled by the WebSocket listener for other users
    } catch (err) {
      console.error('Error adding reaction:', err);
      setError('Failed to add reaction. Please try again.');
      // Revert optimistic update on error
      fetchMessages();
    }
  };
  
  // Handle removing emoji reaction
  const handleRemoveReaction = async (messageId, emoji) => {
    try {
      // Optimistic update
      setMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.id === messageId) {
            const reactions = [...(msg.reactions || [])];
            const existingReactionIndex = reactions.findIndex(r => r.emoji === emoji);
            
            if (existingReactionIndex >= 0) {
              const reaction = reactions[existingReactionIndex];
              // Remove user from reaction
              const filteredUsers = reaction.users.filter(u => u.id !== user?.id);
              
              if (filteredUsers.length > 0) {
                // Update with reduced count
                reactions[existingReactionIndex] = {
                  ...reaction,
                  count: reaction.count - 1,
                  users: filteredUsers
                };
              } else {
                // Remove reaction altogether if no users left
                reactions.splice(existingReactionIndex, 1);
              }
              
              return { ...msg, reactions };
            }
          }
          return msg;
        });
      });
      
      // Make API call
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      await axios.delete(`/api/chat/${messageId}/reactions/${encodeURIComponent(emoji)}`, config);
      // The reaction update will also be handled by the WebSocket listener for other users
    } catch (err) {
      console.error('Error removing reaction:', err);
      setError('Failed to remove reaction. Please try again.');
      // Revert optimistic update on error
      fetchMessages();
    }
  };

  // Switch to team chat
  const switchToTeamChat = () => {
    setIsTeamChat(true);
    setSelectedConversation(null);
    // fetchMessages effect will handle fetching and marking read
  };

  // Start a new DM
  const startNewDM = (targetUser) => {
    // Check if conversation already exists
    const existingConvo = conversations.find(c => c.user_id === targetUser.id);
    setIsTeamChat(false);
    setSelectedConversation(existingConvo || { // Use existing or create temporary structure
      user_id: targetUser.id,
      name: targetUser.name,
      role: targetUser.role,
      last_active: targetUser.last_active,
      last_message: null, // No last message initially
      unread_count: 0
    });
    setShowUserList(false);
     // fetchMessages effect will handle fetching and marking read
  };

  // Open an existing conversation
  const openConversation = (conversation) => {
    setIsTeamChat(false);
    setSelectedConversation(conversation);
     // fetchMessages effect will handle fetching and marking read
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="bg-white shadow-md rounded-lg h-[calc(100vh-32px)] flex">
      {/* Conversations sidebar */}
      <div className="w-64 border-r border-gray-200 flex flex-col">
        <div className="border-b border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900">Conversations</h3>
        </div>

        {/* Team chat button */}
        <button
          onClick={switchToTeamChat}
          className={`flex items-center justify-between w-full p-4 hover:bg-gray-50 border-l-4 ${
            isTeamChat ? 'border-blue-500 bg-blue-50' : 'border-transparent'
          }`}
        >
           <div className="flex items-center">
            <div className="bg-blue-100 text-blue-800 p-2 rounded-full mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Team Chat</h4>
              <p className="text-xs text-gray-500">All members</p>
            </div>
          </div>
          {unreadCounts.teamChat > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {unreadCounts.teamChat}
            </span>
          )}
        </button>

        {/* Direct messages section */}
        <div className="p-4 border-t border-gray-200 flex-1 flex flex-col overflow-hidden">
           <div className="flex justify-between items-center mb-2">
              <div className="flex items-center">
                <h4 className="text-sm font-medium text-gray-900">Direct Messages</h4>
                {unreadCounts.dm > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full ml-2">
                    {unreadCounts.dm}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowUserList(!showUserList)}
                data-userlist-toggle="true"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </button>
           </div>
           <div className="overflow-y-auto flex-1"> {/* Make list scrollable */}
             {conversations.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">No direct messages yet</p>
              ) : (
                <ConversationsList
                  conversations={conversations}
                  selectedId={selectedConversation?.user_id}
                  onSelect={openConversation}
                  currentUser={user}
                />
              )}
           </div>
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col relative"> {/* Added relative positioning */}
         <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            {isTeamChat ? (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Team Chat</h3>
                <p className="text-sm text-gray-500">Stay connected with your team</p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  {selectedConversation?.name || 'Select Conversation'}
                </h3>
                <p className="text-sm text-gray-500 capitalize">
                  {selectedConversation?.role}
                  {selectedConversation?.last_active &&
                   new Date(selectedConversation.last_active) > new Date(Date.now() - 5 * 60 * 1000) &&
                   " • Online"}
                </p>
              </>
            )}
          </div>
          {isTeamChat && (
            <button
              onClick={() => setShowUserList(!showUserList)}
              data-userlist-toggle="true"
              className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              {chatUsers.length} Members
            </button>
          )}
        </div>

        {/* User list panel */}
        {showUserList && (
          <div ref={userListRef} className="absolute right-0 top-16 mt-1 w-64 bg-white border border-gray-200 shadow-lg rounded-lg z-10 overflow-y-auto max-h-[calc(100vh-160px)]"> {/* Adjusted top */}
             <div className="p-3 border-b border-gray-200">
              <h4 className="font-medium text-gray-900">
                {isTeamChat ? 'Chat Members' : 'Start New Conversation'}
              </h4>
            </div>
            <ul className="divide-y divide-gray-100">
              {chatUsers.length === 0 ? (
                <li className="p-3">
                  <p className="text-sm text-gray-500 text-center">No users available</p>
                </li>
              ) : (
                chatUsers.map(chatUser => (
                  <li
                    key={chatUser.id}
                    className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => startNewDM(chatUser)}
                  >
                    <div className="flex items-center">
                      <div className={`h-2 w-2 rounded-full mr-2 ${
                        chatUser.last_active && new Date(chatUser.last_active) > new Date(Date.now() - 5 * 60 * 1000)
                        ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <span className="text-sm text-gray-900">
                        {chatUser.id === user?.id ? `${chatUser.name} (You)` : chatUser.name}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 capitalize">{chatUser.role}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 m-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
          </div>
        )}

        {/* Chat messages container */}
        <MessageList
          ref={messagesContainerRef}
          messages={messages}
          loading={loading}
          user={user}
          formatTimestamp={formatTimestamp}
          onDeleteMessage={handleDeleteMessage}
          onAddReaction={handleAddReaction}
          onRemoveReaction={handleRemoveReaction}
          messagesEndRef={messagesEndRef}
        />

        {/* Message input form */}
        <div className="border-t border-gray-200 px-6 py-4">
          <form onSubmit={handleSendMessage} className="flex">
             <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={isTeamChat
                ? "Type your message to everyone here..."
                : `Type your message to ${selectedConversation?.name || '...'} here...`
              }
              className="flex-1 focus:ring-blue-500 focus:border-blue-500 block w-full rounded-md sm:text-sm border border-gray-300 py-2 px-3"
              disabled={!isConnected} // Disable input if socket is not connected
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || !isConnected} // Disable button if no message or not connected
              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatRoom;
